/**
 * Header unread-message totals (PF-113).
 *
 * GET /conversations/unread-count with a 30 s staleTime. New inbound traffic
 * arrives on `message:new`; marking a thread read happens on MessagesPage.
 * No polling — the query refetches after those invalidations.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { queryKeys } from './queryKeys';
import { useWebSocket } from './useWebSocket';

export const UNREAD_MESSAGES_STALE_TIME_MS = 30_000;

export interface UnreadMessagesCount {
  total: number;
  conversations: number;
}

export function useUnreadMessagesCount() {
  const queryClient = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('messages.read'));

  const query = useQuery({
    queryKey: queryKeys.conversations.unreadCount(),
    queryFn: () => api.get<UnreadMessagesCount>('/conversations/unread-count'),
    staleTime: UNREAD_MESSAGES_STALE_TIME_MS,
    enabled: canRead,
  });

  const onMessageNew = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.conversations.unreadCount() });
  }, [queryClient]);

  useWebSocket('message:new', onMessageNew, { enabled: canRead });

  return query;
}
